/* eslint-disable @typescript-eslint/no-unused-vars */
import { QuestionCircleOutlined } from '@ant-design/icons';
import getFormatMessage from '../../../../locales/get-format-message';
import styles from './index.module.scss';
import {
  CommonAddButton,
  CommonButton,
  CommonButtonGroup,
} from '../../../../components/button/main';
import { FilterGroup } from '../../../../components/filter-group';
import { hasAuthPerm, NOTICE_STATUS_OPTIONS, renderStatusTag } from '../lib';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { closeMyOa, getMyOaList, getNoticeList } from '../../../../api';
import {
  IApiMyoaWorkItem,
  IApiNoticeItem,
  NoticeSystemStatus,
} from '../../../../model/api-modules';
import { getLocalTime } from '../../../../lib';
import { CustomPopover } from '../../../../components/custom-popover';
import { PaginationTable } from '../../../../components/pagination-table';

import { useSelector } from 'react-redux';
import { IRootState } from '../../../../store/reducers';
import { getFilterTitle } from '../create/scope-selector/lib';

import { AutoTooltip } from '../../../../components/auto-tooltip';
import { ICommonSupportPageProps } from '../../../../model';
import { wrapRequestArea } from '../../../../lib/http/lib';

export const NoticeList = (props: ICommonSupportPageProps) => {
  const { site } = props;
  const [list, setList] = useState<IApiNoticeItem[]>([]);
  const [filterList, setFilterList] = useState<IApiNoticeItem[]>([]);
  const [myoaList, setMyoaList] = useState<readonly IApiMyoaWorkItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);

  useEffect(() => {
    init();
  }, []);

  const init = async (withLoading = true) => {
    setLoading(withLoading);
    const [list, myoaList] = await Promise.all([
      wrapRequestArea(getNoticeList, site),
      wrapRequestArea(getMyOaList, site),
    ]);

    setLoading(false);
    setList(list);
    setFilterList(list);
    setMyoaList(myoaList);
  };

  const closeMyoa = async (noticeItem: IApiNoticeItem) => {
    await closeMyOa(noticeItem.id);
    init(false);
  };

  return (
    <div className={styles.noticeListPage}>
      <PaginationTable
        loading={loading}
        list={filterList}
        configFields={[
          {
            key: 'Title',
            name: getFormatMessage('notice_subject'),
            tableExtension: {
              renderRow(key, val) {
                return (
                  <AutoTooltip title={val}>
                    <div className="ellipsis">{val}</div>
                  </AutoTooltip>
                );
              },
            },
          },
          {
            key: 'Founder',
            name: getFormatMessage('notice_creator'),
            tableExtension: {
              renderRow(key, val) {
                return String(val).split('@')[0];
              },
            },
          },
          {
            key: 'scope',
            name: 'scope',
            tableExtension: {
              renderHeaderRow() {
                return (
                  <div className={styles.scopeHeader}>
                    <span>{getFormatMessage('notice_scope')}</span>
                    <CustomPopover
                      content={
                        <div className={styles.scopeTip}>
                          {getFormatMessage(
                            '列表中仅展示通知范围所选筛选项，如有手动将部分项目从通知范围中去除，从在通知详情页导出通知项目列表查看。'
                          )}
                        </div>
                      }
                    >
                      <QuestionCircleOutlined />
                    </CustomPopover>
                  </div>
                );
              },
              renderRow(key, val, record) {
                return (
                  <CustomPopover
                    overlayClassName={styles.sdkPopover}
                    content={
                      <div className={styles.sdkFilter}>
                        <div className={styles.sdkFilterCondition}>
                          <div>{getFormatMessage('notice_xlsx_title')}</div>
                          <div>
                            {getFilterTitle(record.ScopeCondition).content}
                          </div>
                        </div>
                      </div>
                    }
                  >
                    <div className="ellipsis">
                      <span>
                        {getFilterTitle(record.ScopeCondition).content}
                      </span>
                    </div>
                  </CustomPopover>
                );
              },
            },
          },
          {
            key: 'Status',
            name: getFormatMessage('notice_status'),
            tableExtension: {
              renderRow(key, val, record) {
                const status = record.Status;
                return renderStatusTag(status);
              },
            },
          },

          {
            key: 'myOaStatus',
            name: getFormatMessage('notice_myoa_reminder'),
            tableExtension: {
              renderRow(key, val, record) {
                const { MyOAEnable: enable } = record;
                if (!enable) {
                  return getFormatMessage('notice_status_off');
                }

                if (record.Status !== NoticeSystemStatus.Published && enable) {
                  return getFormatMessage('opened');
                }

                const matchedMyoaItem = myoaList.find((myoaItem) =>
                  myoaItem.process_inst_id.endsWith(`-${record.id}`)
                );

                if (
                  record.Status === NoticeSystemStatus.Published &&
                  !matchedMyoaItem
                ) {
                  return getFormatMessage('notice_status_closed');
                }

                if (!matchedMyoaItem) {
                  return null;
                }

                return (
                  <div>
                    <span>
                      {getLocalTime(
                        matchedMyoaItem.expired_time_ms,
                        userInfo?.UserSettings?.Timezone
                      )}
                      {getFormatMessage('common_status_step_expired')} |{' '}
                    </span>
                    <CommonButton type="link" onClick={() => closeMyoa(record)}>
                      {getFormatMessage('notice_close_doc')}
                    </CommonButton>
                  </div>
                );
              },
            },
          },
          {
            key: 'ModifyTime',
            name: getFormatMessage('notice_last_update'),
            tableExtension: {
              renderRow(key, val) {
                return getLocalTime(val, userInfo?.UserSettings?.Timezone);
              },
            },
          },
          {
            key: 'operate',
            name: getFormatMessage('operate'),
            tableExtension: {
              renderRow(key, val, record) {
                return (
                  <CommonButtonGroup>
                    <Link to={`/admin/notice/detail/${record.id}?site=${site}`}>
                      <CommonButton type="link">
                        {getFormatMessage('view')}
                      </CommonButton>
                    </Link>

                    {[NoticeSystemStatus.PendingPublic].includes(
                      record.Status
                    ) &&
                      userInfo?.UserID === record.Founder && (
                        <Link
                          to={`/admin/notice/auth/${record.id}?site=${site}`}
                        >
                          <CommonButton type="link">
                            {getFormatMessage('notice_publish')}
                          </CommonButton>
                        </Link>
                      )}

                    {[NoticeSystemStatus.PendingApproval].includes(
                      record.Status
                    ) &&
                      hasAuthPerm() && (
                        <Link
                          to={`/admin/notice/auth/${record.id}?site=${site}`}
                        >
                          <CommonButton type="link">
                            {getFormatMessage('notice_approve')}
                          </CommonButton>
                        </Link>
                      )}

                    {[
                      NoticeSystemStatus.Draft,
                      NoticeSystemStatus.PendingUpdate,
                    ].includes(record.Status) &&
                      userInfo?.UserID === record.Founder && (
                        <Link
                          to={`/admin/notice/update/${record.id}?site=${site}`}
                        >
                          <CommonButton type="link">
                            {getFormatMessage('edit')}
                          </CommonButton>
                        </Link>
                      )}
                  </CommonButtonGroup>
                );
              },
            },
          },
        ]}
        configFilter={{
          filters: [
            {
              name: 'add',
              render: () => (
                <Link to={`/admin/notice/create?site=${site}`}>
                  <CommonAddButton>
                    {getFormatMessage('notice_new')}
                  </CommonAddButton>
                </Link>
              ),
            },
            {
              label: getFormatMessage('notice_status'),
              name: 'Status',
              options: NOTICE_STATUS_OPTIONS,
              defaultValue: NoticeSystemStatus.Default,
              type: 'select',
            },
            {
              label: getFormatMessage('notice_search'),
              name: 'Title',
              placeholder: getFormatMessage('notice_search'),
              type: 'search',
              place: 'right',
            },
          ],
        }}
      />
    </div>
  );
};
